import {
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@up2d8/ui';
import { AdminIcon } from './icons';
import './filter-select.css';

/**
 * Knoppen, badges, tabs, de werkbalk en de keuzes (spec A §5 "Knoppen" t/m "Keuzes").
 */

export type BtnVariant = 'primary' | 'sec' | 'ghost' | 'danger' | 'danger-sec';
export type BtnSize = 'md' | 'sm' | 'lg';

/**
 * De klassen van een knop, voor een `<Link>` die eruitziet als een knop
 * (`<Link className={btnClass({ variant: 'sec' })}>`).
 */
export function btnClass({
  variant = 'primary',
  size = 'md',
  block,
  className,
}: {
  variant?: BtnVariant;
  size?: BtnSize;
  block?: boolean;
  className?: string;
} = {}): string {
  return cn(
    'a-btn',
    variant !== 'primary' && variant,
    size !== 'md' && size,
    block && 'block',
    className,
  );
}

export interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant;
  size?: BtnSize;
  /** Over de volle breedte. */
  block?: boolean;
  /** Een icoon van 16 vóór de tekst (`<AdminIcon name="plus" />`). */
  icon?: ReactNode;
  testId?: string;
}

/**
 * `.a-btn`: 40 hoog, pill, Poppins 600, 14 px, nooit afbreken. Primair `#007A99`; `sec` omlijnd,
 * `ghost` tekstknop, `danger` rood, `danger-sec` wit met rode tekst. `sm` 32, `lg` 48.
 */
export function Btn({
  variant,
  size,
  block,
  icon,
  testId,
  className,
  type = 'button',
  children,
  ...rest
}: BtnProps) {
  return (
    <button
      type={type}
      className={btnClass({ variant, size, block, className })}
      data-testid={testId}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}

/** `.a-iconbtn`: 32×32, rond, icoon 16. `label` is verplicht: het wordt het `aria-label`. */
export function IconBtn({
  label,
  icon,
  testId,
  className,
  type = 'button',
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> & {
  label: string;
  icon: ReactNode;
  testId?: string;
}) {
  return (
    <button
      type={type}
      className={cn('a-iconbtn', className)}
      aria-label={label}
      title={label}
      data-testid={testId}
      {...rest}
    >
      {icon}
    </button>
  );
}

export type BadgeTone = 'neutral' | 'ok' | 'attention' | 'danger' | 'info' | 'navy' | 'solid';

/**
 * `.a-badge`: pill, 11 px, hoofdletters. Altijd met een woord (spec §7: status nooit alleen kleur).
 * `solid` is de volle oranje voor "NIEUW" of "3 OPEN".
 */
export function Badge({
  tone = 'neutral',
  icon,
  testId,
  children,
}: {
  tone?: BadgeTone;
  icon?: ReactNode;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <span className={cn('a-badge', tone !== 'neutral' && tone)} data-testid={testId}>
      {icon}
      {children}
    </span>
  );
}

export interface TabItem<K extends string> {
  key: K;
  label: ReactNode;
  /** Een teller naast de tab (`.a-count`). */
  count?: number;
  countAttention?: boolean;
  testId?: string;
}

/** De ids die `Tabs` en `TabPanel` delen. */
export function tabIds(prefix: string, key: string) {
  return { tab: `${prefix}-tab-${key}`, panel: `${prefix}-panel-${key}` };
}

/**
 * `.a-tabs`: `role="tablist"` met `role="tab"` en `aria-selected`; pijltjes links en rechts gaan
 * naar de vorige en volgende tab (WAI-ARIA, automatische activatie). Zet de inhoud in `TabPanel`
 * met dezelfde `idPrefix`.
 */
export function Tabs<K extends string>({
  label,
  idPrefix,
  tabs,
  value,
  onChange,
}: {
  /** Het `aria-label` van de tablijst. */
  label: string;
  idPrefix: string;
  tabs: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const delta =
      event.key === 'ArrowRight'
        ? 1
        : event.key === 'ArrowLeft'
          ? -1
          : event.key === 'Home'
            ? 0
            : null;
    if (delta === null && event.key !== 'End') return;
    event.preventDefault();
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? tabs.length - 1
          : (index + (delta as number) + tabs.length) % tabs.length;
    const tab = tabs[next];
    if (!tab) return;
    onChange(tab.key);
    refs.current[tab.key]?.focus();
  };

  return (
    <div className="a-tabs" role="tablist" aria-label={label}>
      {tabs.map((tab, index) => {
        const ids = tabIds(idPrefix, tab.key);
        const selected = tab.key === value;
        return (
          <button
            key={tab.key}
            ref={(element) => {
              refs.current[tab.key] = element;
            }}
            type="button"
            role="tab"
            id={ids.tab}
            aria-selected={selected}
            aria-controls={ids.panel}
            tabIndex={selected ? 0 : -1}
            className={cn('a-tab', selected && 'on')}
            data-testid={tab.testId}
            onClick={() => onChange(tab.key)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {tab.label}
            {tab.count !== undefined ? (
              <span className={cn('a-count', tab.countAttention && 'attention')}>{tab.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** De inhoud bij een tab: `role="tabpanel"`, gelabeld door de tab. */
export function TabPanel({
  idPrefix,
  tabKey,
  className,
  children,
}: {
  idPrefix: string;
  tabKey: string;
  className?: string;
  children: ReactNode;
}) {
  const ids = tabIds(idPrefix, tabKey);
  return (
    <div role="tabpanel" id={ids.panel} aria-labelledby={ids.tab} className={className}>
      {children}
    </div>
  );
}

/**
 * `.a-toolbar`: bovenaan een lijstkaart. Zoekveld en filterknoppen als kinderen; `count` komt
 * rechts ("52 resultaten").
 */
export function Toolbar({ count, children }: { count?: ReactNode; children: ReactNode }) {
  return (
    <div className="a-toolbar">
      {children}
      {count !== undefined ? (
        <span className="a-small a-muted" style={{ marginLeft: 'auto' }} aria-live="polite">
          {count}
        </span>
      ) : null}
    </div>
  );
}

/** `.a-search`: pill, 40 hoog, achtergrond `#F5F7FA`, icoon links. `label` wordt het `aria-label`. */
export function SearchField({
  label,
  placeholder,
  value,
  onChange,
  testId,
  className,
}: {
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  testId?: string;
  className?: string;
}) {
  return (
    <div className={cn('a-search', className)}>
      <AdminIcon name="search" size={16} />
      <input
        type="search"
        aria-label={label}
        placeholder={placeholder ?? label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
      />
    </div>
  );
}

/**
 * `.a-filter`: 36 hoog, "Status: **In dienst**" met chevron; `active` = blauwe tint en rand. Wat er
 * bij een klik opent (een menu, een paneel), beslist het scherm.
 */
export function FilterButton({
  label,
  value,
  active,
  expanded,
  testId,
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value' | 'children'> & {
  label: ReactNode;
  value?: ReactNode;
  active?: boolean;
  expanded?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      className={cn('a-filter', active && 'on')}
      aria-expanded={expanded}
      data-testid={testId}
      {...rest}
    >
      {label}
      {value !== undefined ? (
        <>
          : <b>{value}</b>
        </>
      ) : null}
      <AdminIcon name="chevronDown" size={14} />
    </button>
  );
}

export interface FilterOption {
  value: string;
  label: string;
}

/**
 * Een filterknop (`.a-filter`) die een gewone keuzelijst is: "Status" met een pijltje, en na een
 * keuze "Status: **Open**" in de blauwe tint.
 *
 * De `<select>` ligt onzichtbaar over de knop (`filter-select.css`). Zo werken toetsenbord,
 * schermlezer, telefoon en `selectOption` in een test zonder een eigen menu, en ziet het er toch
 * uit als `FilterButton`. De zichtbare tekst is `aria-hidden`: de keuzelijst heeft het label al en
 * leest zelf haar waarde voor.
 *
 * Twee vormen, uit de werkvloer (BHR-6) en Opvolging (BHR-7), die tot OPR-2 elk een eigen kopie
 * hadden:
 * - `allLabel` gegeven: de eerste keuze is "alles" met waarde `''` ("Alle formulieren"), de
 *   `options` zijn alleen de echte filters.
 * - zonder `allLabel`: de `options` zijn de volledige lijst, en `neutral` zegt welke waarde
 *   "niet gefilterd" is (dan geen tint en alleen het label, zoals "Soort" in W2).
 */
export function FilterSelect({
  label,
  value,
  options,
  onChange,
  allLabel,
  neutral = '',
  testId,
}: {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  /** Een eerste keuze zonder filter, met waarde `''`. */
  allLabel?: string;
  /** De waarde die "niet gefilterd" betekent. */
  neutral?: string;
  testId?: string;
}) {
  const current = options.find((option) => option.value === value);
  const active = value !== (allLabel !== undefined ? '' : neutral) && current !== undefined;
  return (
    <span className={cn('a-filter a-filter-select', active && 'on')}>
      <span aria-hidden="true">
        {label}
        {active ? ':' : null}
      </span>
      {active ? <b aria-hidden="true">{current.label}</b> : null}
      <AdminIcon name="chevronDown" size={14} />
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
      >
        {allLabel !== undefined ? <option value="">{allLabel}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </span>
  );
}

export interface SegOption<V extends string> {
  value: V;
  label: ReactNode;
  testId?: string;
}

/**
 * `.a-seg`: een pill-groep met knoppen van 32 hoog; gekozen = blauwe tint. Zoals de mockup een
 * `radiogroup` met `role="radio"`.
 */
export function Segmented<V extends string>({
  label,
  labelledBy,
  options,
  value,
  onChange,
  disabled,
}: {
  /** Het `aria-label` van de groep (of gebruik `labelledBy`). */
  label?: string;
  labelledBy?: string;
  options: SegOption<V>[];
  value: V;
  onChange: (value: V) => void;
  disabled?: boolean;
}) {
  return (
    <div className="a-seg" role="radiogroup" aria-label={label} aria-labelledby={labelledBy}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          className={cn(option.value === value && 'on')}
          disabled={disabled}
          data-testid={option.testId}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** `.a-chip`: 28 hoog, blauwe tint; met `onRemove` een kruisje met een eigen `aria-label`. */
export function Chip({
  icon,
  onRemove,
  testId,
  children,
}: {
  icon?: ReactNode;
  onRemove?: () => void;
  testId?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <span className="a-chip" data-testid={testId}>
      {icon}
      {children}
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label={t('beheer.kit.removeChip', {
            label: typeof children === 'string' ? children : '',
          })}
        >
          <AdminIcon name="x" size={14} />
        </button>
      ) : null}
    </span>
  );
}

/**
 * Een vinkje (`.a-check` + `.a-box`): een echt `<input type="checkbox">` onder het vlak, zodat
 * toetsenbord, formulier en schermlezer gewoon werken.
 */
export function Checkbox({
  checked,
  onChange,
  label,
  hint,
  disabled,
  name,
  testId,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
  name?: string;
  testId?: string;
}) {
  return (
    <label className="a-check">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        name={name}
        data-testid={testId}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className={cn('a-box', checked && 'on')} aria-hidden="true">
        {checked ? <AdminIcon name="check" size={12} strokeWidth={3} /> : null}
      </span>
      <span>
        {label}
        {hint ? (
          <span className="a-hint" style={{ display: 'block' }}>
            {hint}
          </span>
        ) : null}
      </span>
    </label>
  );
}

/** Een keuzerondje (`.a-check` + `.a-radio`), met een echt `<input type="radio">`. */
export function Radio({
  checked,
  onChange,
  label,
  hint,
  name,
  value,
  disabled,
  testId,
}: {
  checked: boolean;
  onChange: () => void;
  label: ReactNode;
  hint?: ReactNode;
  name: string;
  value: string;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <label className="a-check">
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        data-testid={testId}
        onChange={onChange}
      />
      <span className={cn('a-radio', checked && 'on')} aria-hidden="true" />
      <span>
        {label}
        {hint ? (
          <span className="a-hint" style={{ display: 'block' }}>
            {hint}
          </span>
        ) : null}
      </span>
    </label>
  );
}

/**
 * De schakelaar zelf (`.a-toggle`, 40×24): `role="switch"` met `aria-checked`. Geef een `label`
 * (wordt `aria-label`) of `labelledBy`. `locked` is de grijze "staat uit en kan niet aan".
 */
export function Toggle({
  checked,
  onChange,
  label,
  labelledBy,
  describedBy,
  disabled,
  locked,
  testId,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  labelledBy?: string;
  describedBy?: string;
  disabled?: boolean;
  locked?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      disabled={disabled || locked}
      className={cn('a-toggle', checked && 'on', locked && !checked && 'off-locked')}
      data-testid={testId}
      onClick={() => onChange(!checked)}
    />
  );
}

/** `.a-switch`: een rij met titel, uitleg en rechts de schakelaar. */
export function SwitchRow({
  title,
  text,
  checked,
  onChange,
  disabled,
  locked,
  testId,
}: {
  title: ReactNode;
  text?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  locked?: boolean;
  testId?: string;
}) {
  const id = useId();
  return (
    <div className="a-switch">
      <div className="a-switch-text">
        <div className="a-switch-title" id={`${id}-title`}>
          {title}
        </div>
        {text ? (
          <div className="a-hint" id={`${id}-text`}>
            {text}
          </div>
        ) : null}
      </div>
      <Toggle
        checked={checked}
        onChange={onChange}
        labelledBy={`${id}-title`}
        describedBy={text ? `${id}-text` : undefined}
        disabled={disabled}
        locked={locked}
        testId={testId}
      />
    </div>
  );
}
